import React from 'react';
import {Box, Typography} from '@mui/material';
import ClassDashboardShell from '../components/ClassDashboard.comp';
import NoDataAvailable from '../../../components/NoDataAvailable';
import CircularLoader from '../../../components/CircularLoader';
import {useDashboard} from '../../../context/DashboardContext';
import {isSuppressedCell, isSuppressedItem} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';
import {CONTEXT_BAND_COLORS} from "../../../utils/constants";

import {BandChip} from '../../DashboardCommon/widgets.comp';
const NAVY   = '#052F44';

/* Legend for the "Level" column's chip colors — same CONTEXT_BAND_COLORS
   rubric used for every row below, shown once instead of only inline per
   row, matching the band-chip legends already added to Summary and
   Readiness. */
const CONTEXT_BAND_ORDER = ['Strong', 'Adequate', 'Limited', 'Low'];
const BandLegendChips = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
        {CONTEXT_BAND_ORDER.map((band) => (
            <Box key={band} sx={{
                backgroundColor: CONTEXT_BAND_COLORS[band], color: '#fff',
                fontSize: '11px', fontWeight: 700, px: 1.2, py: 0.3, borderRadius: '20px', whiteSpace: 'nowrap',
            }}>
                {band}
            </Box>
        ))}
    </Box>
);

/* Fixed "what it means" copy per Context Area × band — the third column is
   looked up from here using the label + band that come back from the API. */
const CONTEXT_MEANINGS = {
    'Digital Access at Home and School': {
        Strong: 'Students have reliable access to both suitable digital devices and internet connectivity across home and school.',
        Adequate: 'Students generally have access to devices and internet, with minor limits in reliability, sharing, or setting.',
        Limited: 'Students have partial access, with clear limitations in either device availability or internet connectivity.',
        Low: 'Students have low or unreliable access to devices and/or internet, limiting digital participation.',
    },
    'Youth Perception of Home and Support Environment': {
        Strong: 'Students have strong emotional support perception, clear preference for offline relationships, and high trust in obtaining cyber-risk support.',
        Adequate: 'Students have generally positive perceptions, preferring offline interactions, and confident in available support.',
        Limited: 'Students have mixed perceptions, inconsistent preference for offline interactions, and occasional uncertainty about support availability.',
        Low: 'Students have negative perceptions of emotional support, clear preference for online interactions, and low confidence in obtaining cyber-risk help.',
    },
    'Youth Perception of Parental Mediation': {
        Strong: 'Students perceive highly effective parental mediation, regular and clear safety discussions, and well-implemented digital restrictions.',
        Adequate: 'Students perceive generally effective parental mediation and consistent safety conversations, with minor inconsistencies in restrictions.',
        Limited: 'Students perceive occasionally effective mediation and discussions, frequently noting unclear or inconsistent restrictions.',
        Low: 'Students percevie minimal parental mediation, rarely effective safety conversations, and significantly unclear or ineffective digital restrictions.',
    },
    'Youth Perception of School AI-Digital Literacy Education': {
        Strong: 'Students have strong perception of consistent, comprehensive education across all AI and digital areas.',
        Adequate: 'Students generally perceive regular effective education, occasionally noting gaps in certain digital skill areas.',
        Limited: 'Students partially perceive adequate education, frequently identifying gaps in digital skill areas.',
        Low: 'Students minimally perceive effective digital education, significantly noting absence or inadequacy in AI and digital skill education.',
    },
};

const USE_WITH_CARE_TEXT = 'Context describes support conditions, not performance. Use it to interpret scores fairly and to plan support — never to excuse or to label the class.';
/* Guidance built from this class's own bands. It used to be one fixed
   paragraph ("school education is low, home support is limited") shown to
   every class whatever its scores. */
const listNames = (names) => (names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]);
const buildGuidance = (rows) => {
    if (!rows.length) return '';
    // A class under the n floor has no levels to read.
    if (rows.every((r) => r.suppressed)) return 'Context & support levels are not shown for this class — fewer than 10 students completed the assessment.';
    const low = rows.filter(r => r.level === 'Low').map(r => r.factor);
    const limited = rows.filter(r => r.level === 'Limited').map(r => r.factor);
    if (!low.length && !limited.length) {
        return 'No context area is Limited or Low — support conditions are not a barrier for this class. Keep the current routines in place.';
    }
    const parts = [];
    if (low.length) parts.push(`${listNames(low)} ${low.length > 1 ? 'are' : 'is'} Low`);
    if (limited.length) parts.push(`${listNames(limited)} ${limited.length > 1 ? 'are' : 'is'} Limited`);
    return `${parts.join(', and ')} — frame the results with this in mind, and plan support there first.`;
};
const GuidanceBox = ({text}) => (
    <Box sx={{
        border: '1px solid #C7D9E8', borderLeft: `4px solid ${NAVY}`, borderRadius: '8px',
        backgroundColor: 'var(--brand-surface-subtle, #F0F6FB)', p: 2.5,
    }}>
        <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY, mb: 1}}>
            Decision guidance:
        </Typography>
        <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>
            {text}
        </Typography>
    </Box>
);

const CONTEXT_EQUITY_SECTION_CODE = 'S_6';
const CARD_SECTIONS = [CONTEXT_EQUITY_SECTION_CODE];

const ContextEquitySection = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return (
            <ClassDashboardShell>
                <CircularLoader />
            </ClassDashboardShell>
        );
    }

    if (!dashboardData) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }

    const s6Section = dashboardData?.cards?.sections?.find(s => s.section_code === CONTEXT_EQUITY_SECTION_CODE);
    if (!s6Section) {
        return (
            <ClassDashboardShell>
                <NoDataAvailable />
            </ClassDashboardShell>
        );
    }
    const barChartCard = s6Section?.cards?.find(c => c.card_code === 'C_2');

    // Data Map CD-12-01: S_6 C_2 is a TableCard — Context Area · Score · Level.
    // The page used to read it as a bar chart (card.items), which this card
    // does not have, so the real rows never showed. The items branch stays as a
    // fallback for an older card shape.
    const tableCols = barChartCard?.table_objects || [];
    const colByName = (name) => tableCols.find(c => String(c.column_name || '').trim().toLowerCase() === name);
    const areaCol = colByName('context area');
    const levelCol = colByName('level');
    const scoreCol = colByName('score');
    // A row under the n floor (cell_data.suppressed) keeps its area but shows no level or meaning.
    const toRow = (label, band, suppressed = false) => ({
        factor: label,
        suppressed,
        level: suppressed ? null : band,
        meaning: suppressed ? '' : (CONTEXT_MEANINGS[String(label || '').trim()]?.[band] || ''),
    });
    const dynamicRows = areaCol
        ? (areaCol.values || []).map((label, i) => toRow(
            label,
            levelCol?.values?.[i] || scoreCol?.cell_data?.[i]?.band || levelCol?.cell_data?.[i]?.band,
            isSuppressedCell(scoreCol?.cell_data?.[i]) || isSuppressedCell(levelCol?.cell_data?.[i]),
        ))
        : (barChartCard?.items || []).map(item => toRow(item.label, item.band || item.data?.band, isSuppressedItem(item)));

    // No sample rows when the class has no context data — the page says so instead.
    const contextRows = dynamicRows;
    const guidanceText = buildGuidance(contextRows);

    return (
    <ClassDashboardShell>
        <Box sx={{mb: 3}}>
            <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.2, mb: 0.3}}>
                Class Context & Support Overview
            </Typography>
            <Typography sx={{fontSize: '14px', color: '#4B5563', fontStyle: 'italic', mb: 0.8}}>
                What support conditions may affect this class?
            </Typography>
        </Box>

        <Box sx={{border: '1px solid #FECACA', borderLeft: '4px solid #DC2626', borderRadius: '8px', backgroundColor: 'var(--brand-surface-subtle, #FEE2E2)', p: 2.5, mb: 3}}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                <strong style={{color: '#DC2626'}}>Use with care. </strong>
                {USE_WITH_CARE_TEXT}
            </Typography>
        </Box>

        {/* Rubric legend — sits above the table card rather than as a strip
            inside it, matching how the legend reads on Summary/Readiness. */}
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1.5}}>
            <BandLegendChips/>
        </Box>

        <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', mb: 3}}>
            {/* Table header */}
            <Box sx={{display: 'grid', gridTemplateColumns: '280px 120px 1fr', gap: 1.5, backgroundColor: NAVY, px: 2.5, py: 1.4}}>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#ffffff'}}>Context Area</Typography>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#ffffff'}}>Level</Typography>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#ffffff'}}>What It Means Here</Typography>
            </Box>

            {/* Table rows */}
            {contextRows.length === 0 && (
                <Typography sx={{fontSize: '13px', color: '#6A7282', px: 2.5, py: 2}}>
                    No context & support results for this class yet. They appear once students complete the COS Children Assessment.
                </Typography>
            )}
            {contextRows.map((row, i) => (
                <Box key={row.factor} sx={{
                    display: 'grid', gridTemplateColumns: '280px 120px 1fr', alignItems: 'flex-start', gap: 1.5,
                    px: 2.5, py: 1.6, backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)',
                    borderTop: '1px solid #F3F4F6',
                }}>
                    <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, lineHeight: 1.4}}>{row.factor}</Typography>
                    {row.suppressed ? <Box sx={{justifySelf: 'start'}}><SuppressedValue variant="cell"/></Box> : (row.level ? (
                        <BandChip label={row.level} color={CONTEXT_BAND_COLORS[row.level] || '#6B7280'} sx={{justifySelf: 'start'}}/>
                    ) : <Typography sx={{fontSize: '13px', color: '#9CA3AF'}}>—</Typography>)}
                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.4}}>{row.meaning}</Typography>
                </Box>
            ))}
        </Box>

        {guidanceText && <GuidanceBox text={guidanceText}/>}
    </ClassDashboardShell>
    );
};

export default ContextEquitySection;
